import { useEffect, useState } from "react";
import { X, FileText, Plus, FileDown, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export default function InvoicesPanel({ role, onClose }) {
  const { t } = useI18n();
  const canEdit = role !== "odczyt";
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [cid, setCid] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/invoices").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
  }, []);

  const dlPdf = async (iid) => {
    try {
      const r = await api.get(`/invoices/${iid}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Nie udało się pobrać PDF"); }
  };
  const gen = async () => {
    if (!cid) { toast.error("Wybierz klienta"); return; }
    setBusy(true);
    try { await api.post("/invoices/generate", { client_id: cid }); toast.success("Faktura wystawiona"); setCid(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (iid) => {
    if (!window.confirm("Usunąć fakturę?")) return;
    try { await api.delete(`/invoices/${iid}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="invoices-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2 font-heading font-bold text-white"><FileText size={18} className="text-cyan-400" /> {t("Faktury")}</div>
        <button onClick={onClose} data-testid="invoices-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>
      {canEdit && (
        <div className="px-4 py-3 border-b border-slate-800 flex gap-2">
          <select value={cid} onChange={(e) => setCid(e.target.value)} data-testid="invoice-client" className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-2 text-sm text-white">
            <option value="">{t("— wybierz klienta —")}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={gen} disabled={busy} data-testid="invoice-generate" className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white rounded px-3 text-sm flex items-center gap-1.5">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Wystaw</button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {rows.length === 0 ? <div className="text-sm text-slate-500 text-center py-10">{t("Brak faktur. Faktura tworzy się automatycznie przy dodaniu klienta (jeśli usługi mają ceny).")}</div> : rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-slate-700 bg-slate-800/40 p-3" data-testid={`invoice-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-sm text-white">{r.invoice_no}</div>
                <div className="text-xs text-slate-400 mt-0.5">{r.client_name} · {r.issue_date}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-cyan-300">{Number(r.gross).toFixed(2)} zł</div>
                <div className="text-[10px] text-slate-500">netto {Number(r.net).toFixed(2)} + VAT {r.vat_rate}%</div>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={() => dlPdf(r.id)} data-testid={`invoice-pdf-${r.id}`} className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded px-3 py-1.5 flex items-center gap-1"><FileDown size={13} /> PDF</button>
              {role === "admin" && <button onClick={() => del(r.id)} data-testid={`invoice-del-${r.id}`} className="text-xs bg-slate-800 hover:bg-red-900/40 text-red-300 rounded px-3 py-1.5 flex items-center gap-1"><Trash2 size={13} /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
